import type { TrailDefinition, TrailContext } from './types'

interface NeonPoint {
  x: number
  y: number
  age: number
}

const MAX_POINTS = 30
const POINT_LIFETIME = 0.4

export const neon: TrailDefinition = {
  id: 'neon',
  name: 'Neon',

  init() {
    return { points: [] as NeonPoint[], lastX: 0, lastY: 0 }
  },

  update(state: unknown, ctx: TrailContext) {
    const s = state as { points: NeonPoint[], lastX: number, lastY: number }
    const dt = ctx.deltaTime / 1000

    // Add new point if mouse moved enough
    if (ctx.mouseActive && dt > 0) {
      const dx = ctx.mouseX - s.lastX
      const dy = ctx.mouseY - s.lastY
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist > 3 || s.points.length === 0) {
        if (s.points.length >= MAX_POINTS) {
          s.points.shift()
        }
        s.points.push({ x: ctx.mouseX, y: ctx.mouseY, age: 0 })
        s.lastX = ctx.mouseX
        s.lastY = ctx.mouseY
      }
    }

    // Age points
    for (let i = s.points.length - 1; i >= 0; i--) {
      s.points[i].age += dt
      if (s.points[i].age > POINT_LIFETIME) {
        s.points.splice(i, 1)
      }
    }
  },

  draw(state: unknown, ctx: TrailContext) {
    const s = state as { points: NeonPoint[] }
    const c = ctx.ctx
    const t = ctx.time / 1000

    if (s.points.length < 2) return

    // Cycle neon color
    const hue = (t * 60) % 360

    // Draw multiple passes for glow effect
    const passes = [
      { width: 12, alpha: 0.08 },
      { width: 6, alpha: 0.15 },
      { width: 2, alpha: 0.6 },
      { width: 1, alpha: 1 }
    ]

    for (const pass of passes) {
      c.beginPath()
      c.moveTo(s.points[0].x, s.points[0].y)

      // Smooth curve through points
      for (let i = 1; i < s.points.length; i++) {
        const prev = s.points[i - 1]
        const curr = s.points[i]
        const midX = (prev.x + curr.x) / 2
        const midY = (prev.y + curr.y) / 2
        c.quadraticCurveTo(prev.x, prev.y, midX, midY)
      }

      // Line to last point
      const last = s.points[s.points.length - 1]
      c.lineTo(last.x, last.y)

      c.strokeStyle = `hsla(${hue}, 100%, 70%, ${pass.alpha})`
      c.lineWidth = pass.width
      c.lineCap = 'round'
      c.lineJoin = 'round'
      c.stroke()
    }

    // Draw bright dot at newest point
    const newest = s.points[s.points.length - 1]
    if (newest) {
      const gradient = c.createRadialGradient(newest.x, newest.y, 0, newest.x, newest.y, 8)
      gradient.addColorStop(0, `hsla(${hue}, 100%, 85%, 0.6)`)
      gradient.addColorStop(1, 'transparent')
      c.fillStyle = gradient
      c.fillRect(newest.x - 8, newest.y - 8, 16, 16)
    }
  }
}
